import type { Partner } from "@expert/shared-types";

import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { getLogger, type Logger } from "@expert/logging";
import { QueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";

import { getHashedValue } from "@/shared-utils/crypto/getHashedValue";

import type { DataKeys, DataKeysContext, EncryptionInfo } from "./types";

import { decrypt, decryptDataKey, encrypt, generateDataKey } from "./actions";

export type EncryptionData = EncryptionInfo & Pick<DataKeys, "encryptedDataKey">;

const ENCRYPTION_ROOT = "encryption" as const;
const DECRYPT_SEGMENT = "decrypt" as const;
const GENERATE_SEGMENT = "generate" as const;

const GC_MS = 1000 * 60 * 60 * 2; // 2 hours — covers long chats; evictDataKeys handles teardown

/**
 * Dedicated QueryClient for encryption data keys. Isolated from the app-wide QueryClient so that
 * global cache resets (e.g. on logout) don't accidentally nuke in-flight encryption keys mid-chat.
 */
const dataKeyQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 2,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10_000),
            staleTime: Number.POSITIVE_INFINITY,
            gcTime: GC_MS,
        },
    },
});

/** Exposed for tests that need to inspect or reset the internal cache. */
export function getDataKeyQueryClient(): QueryClient {
    return dataKeyQueryClient;
}

// Cap response bodies: KMS error responses may echo wrapped key material; never log url (carries encryptedDataKey).
const MAX_RESPONSE_BODY_CHARS = 200;

/**
 * Extracts safe-to-log fields from a decrypt/generate failure.
 * For a ky `HTTPError`, returns `method`/`status`/`statusText`/`responseBody` only, never the
 * request url or raw message, which carry `?encryptedDataKey=`. For any other error (e.g. a crypto
 * failure or `TypeError`), returns `errorName` and `errorMessage`, which are safe and diagnostic.
 */
export function extractHttpErrorFields(error: unknown): Record<string, unknown> {
    // Guard request/response too: a well-formed ky HTTPError always has them, but destructuring a
    // malformed one below would throw out of this helper. Fall back to the safe name/message shape.
    if (!(error instanceof HTTPError) || !error.request || !error.response) {
        return {
            errorName: error instanceof Error ? error.name : "Unknown",
            errorMessage: error instanceof Error ? error.message : String(error),
        };
    }
    const {
        request: { method },
        response: { status, statusText },
    } = error;
    // ky 2 has already read the body onto `data` (text, or parsed JSON) and spent the response,
    // so `error.response.clone()` would throw (ew#7426).
    const { data } = error;
    const bodyText = typeof data === "string" || data === undefined ? data : JSON.stringify(data);
    const responseBody = bodyText?.slice(0, MAX_RESPONSE_BODY_CHARS) || undefined;
    return { method, status, statusText, responseBody, errorName: "HTTPError" };
}

/**
 * Wraps a fetch function so that React Query retry attempts are logged.
 * The first call is silent; subsequent calls (retries) log at warn level.
 */
function withRetryLogging<T>(logger: Logger, operation: string, fn: () => Promise<T>): () => Promise<T> {
    let attempt = 0;
    return async () => {
        attempt++;
        if (attempt > 1) {
            logger.warn({ attempt, operation }, "Retrying data key operation");
        }
        return fn();
    };
}

async function encryptedDataKeyFingerprint(encryptedDataKey: string): Promise<string> {
    return getHashedValue(encryptedDataKey, "SHA-256");
}

function decryptQueryKey(chatId: string, partner: Partner, fingerprint: string) {
    return [ENCRYPTION_ROOT, DECRYPT_SEGMENT, chatId, partner, fingerprint] as const;
}

function generateQueryKey(chatId: string, partner: Partner) {
    return [ENCRYPTION_ROOT, GENERATE_SEGMENT, chatId, partner] as const;
}

/**
 * Deduplicated decrypt-data-key fetch via TanStack Query. Returns a closure that decrypts
 * individual `EncryptionInfo` payloads using the resolved plaintext key.
 */
export async function decryptWithCache(
    { chatId, partner }: DataKeysContext,
    encryptedDataKey: string,
): Promise<(info: EncryptionInfo) => Promise<string>> {
    const logger = getLogger({ module: "dataKeyService.decrypt", chatId, partner });

    const fingerprint = await encryptedDataKeyFingerprint(encryptedDataKey);
    const shortFingerprint = fingerprint.slice(0, 8);

    try {
        const dataKeys = await dataKeyQueryClient.fetchQuery({
            queryKey: decryptQueryKey(chatId, partner, fingerprint),
            queryFn: withRetryLogging(logger, "decryptDataKey", () =>
                decryptDataKey(partner, chatId, encryptedDataKey),
            ),
        });

        logger.debug({ shortFingerprint }, "Data key decrypt succeeded");

        return (info: EncryptionInfo) =>
            decrypt(info, { plaintextDataKey: dataKeys.plaintextDataKey, context: chatId });
    } catch (error) {
        // Do not log the raw error: a ky HTTPError carries the request url (with ?encryptedDataKey=) in its message.
        const httpFields = extractHttpErrorFields(error);
        logger.error({ ...httpFields, shortFingerprint, chatId, partner }, "Data key decrypt failed after retries");
        void sendErrorMetric("DataKeyDecryptFailure", { partner });
        throw error;
    }
}

/**
 * Returns an encrypt closure for the given chat context.
 *
 * Uses a key already cached by `decryptWithCache` — i.e. a key that originated from an inbound
 * customer message. This is the normal steady-state: the expert decrypts the transcript first,
 * and outbound replies reuse the same key material.
 *
 * Falls back to `generateDataKey` when no decrypt entry exists yet (e.g. expert sends the first
 * message before any customer messages arrive).
 */
export async function encryptWithCache({
    chatId,
    partner,
}: DataKeysContext): Promise<(plaintextData: string) => Promise<EncryptionData>> {
    const logger = getLogger({ module: "dataKeyService.encrypt", chatId, partner });

    const cached = dataKeyQueryClient
        .getQueryCache()
        .findAll({ queryKey: [ENCRYPTION_ROOT, DECRYPT_SEGMENT, chatId] })
        .find((query) => query.state.status === "success");

    if (!cached?.state.data) {
        const pendingCount = dataKeyQueryClient
            .getQueryCache()
            .findAll({ queryKey: [ENCRYPTION_ROOT, DECRYPT_SEGMENT, chatId] })
            .filter((query) => query.state.fetchStatus === "fetching").length;

        logger.info(
            { chatId, partner, pendingDecrypts: pendingCount },
            "No cached decrypt key — generating a new data key",
        );
        try {
            const dataKeys = await dataKeyQueryClient.fetchQuery({
                queryKey: generateQueryKey(chatId, partner),
                queryFn: withRetryLogging(logger, "generateDataKey", () => generateDataKey(partner, chatId)),
            });
            logger.debug("Data key generate succeeded");
            return async (plaintextData: string) => ({
                ...(await encrypt(plaintextData, { plaintextDataKey: dataKeys.plaintextDataKey, context: chatId })),
                encryptedDataKey: dataKeys.encryptedDataKey,
            });
        } catch (error) {
            const httpFields = extractHttpErrorFields(error);
            logger.warn({ ...httpFields, chatId, partner }, "Data key generate failed after retries");
            void sendErrorMetric("DataKeyGenerateFailure", { partner });
            throw error;
        }
    }

    const dataKeys = cached.state.data as DataKeys;
    return async (plaintextData: string) => ({
        ...(await encrypt(plaintextData, { plaintextDataKey: dataKeys.plaintextDataKey, context: chatId })),
        encryptedDataKey: dataKeys.encryptedDataKey,
    });
}

/** Clear cached decrypt and generate entries for the given contexts (e.g. chat teardown). */
export function evictDataKeys(contexts: string[]): void {
    for (const ctx of contexts) {
        dataKeyQueryClient.removeQueries({ queryKey: [ENCRYPTION_ROOT, DECRYPT_SEGMENT, ctx] });
        dataKeyQueryClient.removeQueries({ queryKey: [ENCRYPTION_ROOT, GENERATE_SEGMENT, ctx] });
    }
}
